import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LogOut, Globe } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { Avatar, AvatarFallback } from '../ui/avatar';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { cn, isRTL } from '../../lib/utils';
import apiService from '../../services/api';
import { NotificationDropdown } from '../NotificationDropdown';

interface NavbarProps {
  isCollapsed: boolean;
}

const Navbar: React.FC<NavbarProps> = ({ isCollapsed }) => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [logoutDialogOpen, setLogoutDialogOpen] = React.useState(false);
  const user = apiService.getCurrentUser();
  const rtl = isRTL(i18n.language);

  const handleLogout = () => {
    apiService.logout();
    navigate('/login');
  };

  const changeLanguage = (lng: string) => {
    i18n.changeLanguage(lng);
    localStorage.setItem('language', lng);
    document.documentElement.dir = lng === 'ar' ? 'rtl' : 'ltr';
    document.documentElement.lang = lng;
  };

  const getUserInitials = () => {
    if (!user?.email) return '?';
    return user.email.charAt(0).toUpperCase();
  };

  const languages = [
    { code: 'ar', name: 'العربية', flag: '🇸🇦' },
    { code: 'en', name: 'English', flag: '🇬🇧' },
  ];

  return (
    <>
      <header
        className={cn(
          'fixed top-0 h-16 bg-background border-b border-border transition-all duration-300 z-30',
          'flex items-center justify-between px-3 sm:px-6',
          rtl ? 'right-0' : 'left-0',
          isCollapsed ? (rtl ? 'left-0 right-20' : 'left-20 right-0') : (rtl ? 'left-0 right-64' : 'left-64 right-0')
        )}
      >
        {/* Title */}
        <div className="flex-1 min-w-0 mr-2">
          <h1 className="text-sm sm:text-lg font-semibold text-foreground truncate">
            {user?.userType === 'admin' ? t('common.adminPanel') : user?.company?.companyName || t('app.title')}
          </h1>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-1 sm:gap-3">
          {/* Notifications */}
          <NotificationDropdown />

          {/* Language Switcher */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="rounded-full h-9 w-9 sm:h-10 sm:w-10">
                <Globe className="w-4 h-4 sm:w-5 sm:h-5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align={rtl ? 'start' : 'end'}>
              <DropdownMenuLabel>{t('common.language') || 'Language'}</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {languages.map((lang) => (
                <DropdownMenuItem
                  key={lang.code}
                  onClick={() => changeLanguage(lang.code)}
                  className={cn(
                    'cursor-pointer',
                    i18n.language === lang.code && 'bg-accent'
                  )}
                >
                  <span className="mr-2">{lang.flag}</span>
                  {lang.name}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          {/* User Menu */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="relative h-9 w-9 sm:h-10 sm:w-10 rounded-full">
                <Avatar className="h-9 w-9 sm:h-10 sm:w-10">
                  <AvatarFallback className="bg-gradient-to-br from-brand-500 to-brand-700 text-white text-sm">
                    {getUserInitials()}
                  </AvatarFallback>
                </Avatar>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align={rtl ? 'start' : 'end'} className="w-56">
              <DropdownMenuLabel>
                <div className="flex flex-col space-y-1">
                  <p className="text-sm font-medium leading-none truncate">{user?.email}</p>
                  <p className="text-xs leading-none text-muted-foreground">
                    {user?.userType === 'admin' ? 'Administrator' : 'Company Manager'}
                  </p>
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => setLogoutDialogOpen(true)} className="cursor-pointer text-destructive">
                <LogOut className={cn('w-4 h-4', rtl ? 'ml-2' : 'mr-2')} />
                {t('auth.logout')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      {/* Logout Confirmation Dialog */}
      <Dialog open={logoutDialogOpen} onOpenChange={setLogoutDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('auth.logout')}</DialogTitle>
            <DialogDescription>
              {t('auth.logoutConfirm')}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setLogoutDialogOpen(false)}>
              {t('auth.cancel')}
            </Button>
            <Button variant="destructive" onClick={handleLogout}>
              {t('auth.yes')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default Navbar;
